import * as THREE from 'three';
import { RunwayDef } from '../world/Airport';

/**
 * Builds a simple but readable runway: grey surface, threshold bars, centerline,
 * edge lights and an approach light system so it "pops" out of the fog on an ILS
 * approach (per ELITE, the active runway's lights are on).
 *
 * The geometry is built in a local frame with the runway centerline along local
 * +Z and landing direction toward +Z, then rotated into the world so that +Z
 * maps to the runway's landing direction, and translated to the runway's true
 * position. World mapping (NED->Three): x = East, y = Up, z = -North.
 *
 * NOTE: the surface uses a flat polygonOffset against a flat ground plane. Once
 * a sloped-terrain mesh replaces the flat ground, the runway surface will need
 * to be conformed to the terrain (offset along the surface normal) or it will
 * z-fight / clip through the ground.
 */
export function buildRunway(rwy: RunwayDef): THREE.Group {
  const g = new THREE.Group();
  const len = rwy.length;
  const wid = rwy.width;

  const white = new THREE.MeshBasicMaterial({ color: 0xf2f2f2 });

  // Surface. Sits just above the ground plane with a polygon offset so it never
  // z-fights the grass, and a mid-grey so it reads clearly against green.
  const surfMat = new THREE.MeshStandardMaterial({
    color: 0x44474d, roughness: 0.95, metalness: 0,
    polygonOffset: true, polygonOffsetFactor: -2, polygonOffsetUnits: -2,
  });
  const surface = new THREE.Mesh(new THREE.PlaneGeometry(wid, len), surfMat);
  surface.rotation.x = -Math.PI / 2;
  surface.position.y = 0.05;
  g.add(surface);

  // Dashed centerline
  const dashLen = 30, gap = 20;
  const nDashes = Math.floor(len / (dashLen + gap));
  for (let i = 0; i < nDashes; i++) {
    const z = -len / 2 + dashLen / 2 + i * (dashLen + gap);
    const dash = new THREE.Mesh(new THREE.PlaneGeometry(0.9, dashLen), white);
    dash.rotation.x = -Math.PI / 2;
    dash.position.set(0, 0.04, z);
    g.add(dash);
  }

  // Threshold bars at both ends
  for (const end of [-1, 1]) {
    for (let s = -4; s <= 4; s++) {
      if (s === 0) continue;
      const bar = new THREE.Mesh(new THREE.PlaneGeometry(1.4, 18), white);
      bar.rotation.x = -Math.PI / 2;
      bar.position.set(s * 3.2, 0.04, end * (len / 2 - 14));
      g.add(bar);
    }
  }

  // Edge lights
  const lightGeo = new THREE.SphereGeometry(0.6, 6, 6);
  const rowStep = 60;
  const nRows = Math.floor(len / rowStep);
  for (let i = 0; i <= nRows; i++) {
    const z = -len / 2 + i * rowStep;
    for (const side of [-1, 1]) {
      const l = new THREE.Mesh(lightGeo, white);
      l.position.set(side * (wid / 2 + 1), 0.6, z);
      g.add(l);
    }
  }

  // Approach lights extending out from the landing (approach) threshold at -Z.
  const appMat = new THREE.MeshBasicMaterial({ color: 0xfff2c0 });
  for (let i = 1; i <= 15; i++) {
    const z = -len / 2 - i * 30;
    const l = new THREE.Mesh(new THREE.SphereGeometry(0.85, 6, 6), appMat);
    l.position.set(0, 0.8, z);
    g.add(l);
    if (i % 5 === 0) {
      for (const s of [-6, -3, 3, 6]) {
        const lb = new THREE.Mesh(new THREE.SphereGeometry(0.7, 6, 6), appMat);
        lb.position.set(s, 0.8, z);
        g.add(lb);
      }
    }
  }

  // Rotate the whole runway so local +Z maps to the landing direction.
  const hdg = (rwy.headingDeg * Math.PI) / 180;
  g.rotation.y = Math.PI - hdg;

  // Translate to the runway's true position: its midpoint in NED (threshold +
  // half-length along the landing direction), lifted to its MSL elevation.
  // NED->Three: x = East, y = Up, z = -North. For the legacy runway 24
  // (midpoint at the NED origin, elevation 0) this is (0,0,0) — unchanged.
  const half = rwy.length / 2;
  const midN = rwy.threshold.n + rwy.dir.n * half;
  const midE = rwy.threshold.e + rwy.dir.e * half;
  g.position.set(midE, rwy.elevation, -midN);

  return g;
}
